import { create } from "zustand";

import { User, UserType } from "@/types/user.types";

type UserIsDeletedInfo = { id: string; name: string; userType: UserType } | null;
type UserLoginStatusInfo = { id: string; disabled: boolean; name: string } | null;
type UserUpdateShiftTypeInfo = { id: string; isDisabled: boolean; name: string } | null;
type UserRestoreInfo = { id: string; name: string; userType: UserType; identityNo?: string } | null;

export type UsersStoreFilters = {
    isDeleted: boolean;
    userType: UserType | null;
};

type UsersTableState = {
    filters: UsersStoreFilters;
    isFullScreen: boolean;
    isDeletedInfo: UserIsDeletedInfo;
    isRestoreInfo: UserRestoreInfo;
    loginStatusInfo: UserLoginStatusInfo;
    shiftTypeInfo: UserUpdateShiftTypeInfo;
    showCreateDialog: boolean;
    showVehicleUpdateDialog: User | null;
    showPasswordUpdateDialog: User | null;
    showUpdateUserTypeDialog: User | null;
    showUpdateUserLifeCycleDialog: User | null;
    showLifecycleInfo: User | null;
};

type UsersTableActions = {
    actions: {
        updateFilters: (filters: Partial<UsersStoreFilters>) => void;
        updateFullScreen: (val?: boolean) => void;
        updateDeletionInfo: (val?: UserIsDeletedInfo) => void;
        updateShowCreateDialog: (val?: boolean) => void;
        updateLoginStatusInfo: (val?: UserLoginStatusInfo) => void;
        updateShiftTypeInfo: (val?: UserUpdateShiftTypeInfo) => void;
        updateShowVehicleUpdateDialog: (val?: User) => void;
        updateShowPasswordUpdateDialog: (val?: User) => void;
        updateShowdUpdateUserTypeDialog: (val?: User) => void;
        updateShowUpdateUserLifeCycleDialog: (val?: User) => void;
        updateRestoreInfo: (val?: UserRestoreInfo) => void;
        updateLifecycleInfo: (val?: User) => void;
    };
};

export const useUsersTableStore = create<UsersTableState & UsersTableActions>()((set, get) => ({
    isFullScreen: false,
    isDeletedInfo: null,
    loginStatusInfo: null,
    showCreateDialog: false,
    showVehicleUpdateDialog: null,
    showPasswordUpdateDialog: null,
    showUpdateUserTypeDialog: null,
    showUpdateUserLifeCycleDialog: null,
    showLifecycleInfo: null,
    isRestoreInfo: null,
    shiftTypeInfo: null,
    filters: {
        isDeleted: true,
        userType: null,
    },
    actions: {
        updateFilters: (filters) => set({ filters: { ...get().filters, ...filters } }),
        updateFullScreen: (isFullScreen) => set({ isFullScreen: isFullScreen ?? !get().isFullScreen }),
        updateDeletionInfo: (val) => set({ isDeletedInfo: val ?? null }),
        updateShiftTypeInfo: (val) => set({ shiftTypeInfo: val ?? null }),
        updateShowCreateDialog: (val) => set({ showCreateDialog: val ?? false }),
        updateShowVehicleUpdateDialog: (val) => set({ showVehicleUpdateDialog: val ?? null }),
        updateLoginStatusInfo: (val) => set({ loginStatusInfo: val ?? null }),
        updateShowPasswordUpdateDialog: (val) => set({ showPasswordUpdateDialog: val ?? null }),
        updateShowdUpdateUserTypeDialog: (val) => set({ showUpdateUserTypeDialog: val ?? null }),
        updateShowUpdateUserLifeCycleDialog: (val) => set({ showUpdateUserLifeCycleDialog: val ?? null }),
        updateRestoreInfo: (val) => set({ isRestoreInfo: val ?? null }),
        updateLifecycleInfo: (val) => set({ showLifecycleInfo: val ?? null }),
    },
}));

export const useUsersTableIsFullScreen = () => useUsersTableStore((state) => state.isFullScreen);
export const useUsersTableFilters = () => useUsersTableStore((state) => state.filters);
export const useUsersTableDeletionInfo = () => useUsersTableStore((state) => state.isDeletedInfo);
export const useUsersFilterByKey = (key: keyof UsersStoreFilters) => useUsersTableStore((state) => state.filters[key]);
export const useUsersShowCreateDialog = () => useUsersTableStore((state) => state.showCreateDialog);
export const useUsersShiftTypeInfo = () => useUsersTableStore((state) => state.shiftTypeInfo);
export const useUsersShowVehicleUpdateDialog = () => useUsersTableStore((state) => state.showVehicleUpdateDialog);
export const useUsersShowPasswordUpdateDialog = () => useUsersTableStore((state) => state.showPasswordUpdateDialog);
export const useUsersShowUpdateUserTypeDialog = () => useUsersTableStore((state) => state.showUpdateUserTypeDialog);
export const useUsersUpdateLifeCycleDialog = () => useUsersTableStore((state) => state.showUpdateUserLifeCycleDialog);
export const useUsersRestoreInfo = () => useUsersTableStore((state) => state.isRestoreInfo);
export const useUsersLifecycleInfo = () => useUsersTableStore((state) => state.showLifecycleInfo);

export const userUsersLoginStatusInfo = () => useUsersTableStore((state) => state.loginStatusInfo);

export const useUsersTableActions = () => useUsersTableStore((state) => state.actions);
